import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Chip, LinearProgress,
  TextField, MenuItem, Alert, CircularProgress, Stepper,
  Step, StepLabel, StepContent, Divider, Avatar,
} from '@mui/material';
import { CheckCircle, FlightTakeoff, Send } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiToken(token: string, path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const GENDERS = [
  { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' }, { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const LAPTOP_OS = ['Mac (macOS)', 'Windows', 'Linux', 'No preference'];
const T_SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default function PreBoardingPortal() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [personal, setPersonal] = useState({ preferredName: '', personalEmail: '', phoneNumber: '', dateOfBirth: '', gender: '', address: '', city: '', country: 'Hong Kong', nationality: '', nationalId: '', dietaryRequirements: '', tShirtSize: '' });
  const [bank, setBank] = useState({ bankName: '', bankCode: '', bankAccount: '', bankAccountName: '', bankBranch: '' });
  const [emerg, setEmerg] = useState({ emergencyName: '', emergencyRelationship: '', emergencyPhone: '', emergencyEmail: '' });
  const [equip, setEquip] = useState({ laptopOs: '', peripheralNotes: '' });

  useEffect(() => {
    if (!token) return;
    apiToken(token, `/api/pre-boarding/token/${token}`)
      .then(d => {
        setData(d);
        if (d.status === 'submitted' || d.status === 'verified') setSubmitted(true);
        // Pre-fill if data exists
        setPersonal(p => ({ ...p, preferredName: d.preferredName || '', personalEmail: d.personalEmail || '', phoneNumber: d.phoneNumber || '', dateOfBirth: d.dateOfBirth || '', gender: d.gender || '', address: d.address || '', city: d.city || '', country: d.country || 'Hong Kong', nationality: d.nationality || '', nationalId: d.nationalId || '', dietaryRequirements: d.dietaryRequirements || '', tShirtSize: d.tShirtSize || '' }));
        setBank(b => ({ ...b, bankName: d.bankName || '', bankCode: d.bankCode || '', bankAccount: d.bankAccount || '', bankAccountName: d.bankAccountName || '', bankBranch: d.bankBranch || '' }));
        setEmerg(e => ({ ...e, emergencyName: d.emergencyName || '', emergencyRelationship: d.emergencyRelationship || '', emergencyPhone: d.emergencyPhone || '', emergencyEmail: d.emergencyEmail || '' }));
        setEquip(eq => ({ ...eq, laptopOs: d.laptopOs || '', peripheralNotes: d.peripheralNotes || '' }));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const saveStep = async (step: number) => {
    setSaving(true);
    try {
      if (step === 0) await apiToken(token!, `/api/pre-boarding/token/${token}/personal-info`, { method: 'PATCH', body: JSON.stringify(personal) });
      if (step === 1) await apiToken(token!, `/api/pre-boarding/token/${token}/bank-details`, { method: 'PATCH', body: JSON.stringify(bank) });
      if (step === 2) await apiToken(token!, `/api/pre-boarding/token/${token}/emergency-contact`, { method: 'PATCH', body: JSON.stringify(emerg) });
      if (step === 3) await apiToken(token!, `/api/pre-boarding/token/${token}/equipment`, { method: 'PATCH', body: JSON.stringify(equip) });
      setActiveStep(s => s + 1);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submitAll = async () => {
    setSaving(true);
    try {
      await apiToken(token!, `/api/pre-boarding/token/${token}/submit`, { method: 'POST' });
      setSubmitted(true);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}><CircularProgress /></Box>;
  if (error && !data) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Alert severity="error" sx={{ maxWidth: 480, mx: 'auto' }}>{error}</Alert>
    </Box>
  );

  const emp = data?.employee;
  const pct = data ? Math.round((data.completedTasks / Math.max(data.totalTasks, 1)) * 100) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 6 }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#C08A2E', color: 'white', py: 3, px: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FlightTakeoff sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Welcome to Humanity Protocol</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Let's get you set up before your first day 🎉</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 680, mx: 'auto', mt: 3, px: 2 }}>
        {/* Welcome card */}
        <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #C08A2E' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Hi {emp?.firstName} 👋
          </Typography>
          {data?.welcomeMessage ? (
            <Typography variant="body1">{data.welcomeMessage}</Typography>
          ) : (
            <Typography variant="body1">
              We're thrilled to have you joining as <strong>{emp?.position?.title}</strong> in the <strong>{emp?.department?.name}</strong> team.
              Please complete the steps below before your start date of <strong>{data?.startDate || '—'}</strong>.
            </Typography>
          )}
          {data?.deadline && (
            <Chip size="small" label={`Complete by ${data.deadline}`} sx={{ mt: 1.5, bgcolor: '#fff3e0', color: '#e65100' }} />
          )}
        </Paper>

        {/* Progress */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>Your progress</Typography>
            <Typography variant="body2">{data?.completedTasks}/{data?.totalTasks} tasks · {pct}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct}
            sx={{ height: 10, borderRadius: 5,
              '& .MuiLinearProgress-bar': { bgcolor: pct === 100 ? '#2e7d32' : '#C08A2E' } }} />
        </Paper>

        {submitted ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>All done! 🎉</Typography>
            <Typography variant="body1" color="text.secondary">
              Your pre-boarding information has been submitted. HR will review and be in touch.
              We can't wait to see you on <strong>{data?.startDate || 'your first day'}</strong>!
            </Typography>
          </Paper>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 0: Personal Info */}
              <Step>
                <StepLabel>Personal Information</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Preferred Name" value={personal.preferredName} onChange={e => setPersonal(p => ({ ...p, preferredName: e.target.value }))} placeholder={emp?.firstName} />
                      <TextField select fullWidth label="Gender" value={personal.gender} onChange={e => setPersonal(p => ({ ...p, gender: e.target.value }))}>
                        {GENDERS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Personal Email" type="email" value={personal.personalEmail} onChange={e => setPersonal(p => ({ ...p, personalEmail: e.target.value }))} required />
                      <TextField fullWidth label="Phone Number" value={personal.phoneNumber} onChange={e => setPersonal(p => ({ ...p, phoneNumber: e.target.value }))} required />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} value={personal.dateOfBirth} onChange={e => setPersonal(p => ({ ...p, dateOfBirth: e.target.value }))} />
                      <TextField fullWidth label="Nationality" value={personal.nationality} onChange={e => setPersonal(p => ({ ...p, nationality: e.target.value }))} />
                    </Box>
                    <TextField fullWidth label="HKID / National ID Number" value={personal.nationalId} onChange={e => setPersonal(p => ({ ...p, nationalId: e.target.value }))} />
                    <TextField fullWidth label="Home Address" multiline rows={2} value={personal.address} onChange={e => setPersonal(p => ({ ...p, address: e.target.value }))} required />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="City" value={personal.city} onChange={e => setPersonal(p => ({ ...p, city: e.target.value }))} />
                      <TextField fullWidth label="Country" value={personal.country} onChange={e => setPersonal(p => ({ ...p, country: e.target.value }))} />
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Dietary Requirements (optional)" value={personal.dietaryRequirements} onChange={e => setPersonal(p => ({ ...p, dietaryRequirements: e.target.value }))} placeholder="e.g. vegetarian, halal, nut allergy" />
                      <TextField select fullWidth label="T-Shirt Size (for welcome kit)" value={personal.tShirtSize} onChange={e => setPersonal(p => ({ ...p, tShirtSize: e.target.value }))}>
                        <MenuItem value="">No preference</MenuItem>
                        {T_SHIRT_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Box>
                    <Button variant="contained" onClick={() => saveStep(0)} disabled={saving || !personal.personalEmail}
                      sx={{ alignSelf: 'flex-start', bgcolor: '#C08A2E' }}>
                      {saving ? 'Saving…' : 'Save & Continue'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 1: Bank */}
              <Step>
                <StepLabel>Bank Account Details</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Your salary will be paid directly to this account. Please ensure the details are accurate.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Bank Name" value={bank.bankName} onChange={e => setBank(b => ({ ...b, bankName: e.target.value }))} required placeholder="e.g. HSBC, Hang Seng Bank" />
                      <TextField fullWidth label="Bank Code (HK Clearing)" value={bank.bankCode} onChange={e => setBank(b => ({ ...b, bankCode: e.target.value }))} placeholder="e.g. 004" />
                    </Box>
                    <TextField fullWidth label="Account Number" value={bank.bankAccount} onChange={e => setBank(b => ({ ...b, bankAccount: e.target.value }))} required />
                    <TextField fullWidth label="Account Holder Name (as on bank records)" value={bank.bankAccountName} onChange={e => setBank(b => ({ ...b, bankAccountName: e.target.value }))} required />
                    <TextField fullWidth label="Branch Name (optional)" value={bank.bankBranch} onChange={e => setBank(b => ({ ...b, bankBranch: e.target.value }))} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button onClick={() => setActiveStep(s => s - 1)}>Back</Button>
                      <Button variant="contained" onClick={() => saveStep(1)} disabled={saving || !bank.bankAccount}
                        sx={{ bgcolor: '#C08A2E' }}>
                        {saving ? 'Saving…' : 'Save & Continue'}
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Emergency Contact */}
              <Step>
                <StepLabel>Emergency Contact</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Full Name" value={emerg.emergencyName} onChange={e => setEmerg(em => ({ ...em, emergencyName: e.target.value }))} required />
                      <TextField fullWidth label="Relationship" value={emerg.emergencyRelationship} onChange={e => setEmerg(em => ({ ...em, emergencyRelationship: e.target.value }))} placeholder="e.g. Spouse, Parent" required />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField fullWidth label="Phone Number" value={emerg.emergencyPhone} onChange={e => setEmerg(em => ({ ...em, emergencyPhone: e.target.value }))} required />
                      <TextField fullWidth label="Email (optional)" value={emerg.emergencyEmail} onChange={e => setEmerg(em => ({ ...em, emergencyEmail: e.target.value }))} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button onClick={() => setActiveStep(s => s - 1)}>Back</Button>
                      <Button variant="contained" onClick={() => saveStep(2)} disabled={saving || !emerg.emergencyName}
                        sx={{ bgcolor: '#C08A2E' }}>
                        {saving ? 'Saving…' : 'Save & Continue'}
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Equipment */}
              <Step>
                <StepLabel>Equipment Preferences</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <TextField select fullWidth label="Preferred Laptop OS" value={equip.laptopOs} onChange={e => setEquip(eq => ({ ...eq, laptopOs: e.target.value }))}>
                      {LAPTOP_OS.map(os => <MenuItem key={os} value={os}>{os}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth multiline rows={2} label="Any other peripheral or setup requirements?" value={equip.peripheralNotes} onChange={e => setEquip(eq => ({ ...eq, peripheralNotes: e.target.value }))} placeholder="e.g. external monitor, ergonomic keyboard, standing desk" />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button onClick={() => setActiveStep(s => s - 1)}>Back</Button>
                      <Button variant="contained" onClick={() => saveStep(3)} disabled={saving}
                        sx={{ bgcolor: '#C08A2E' }}>
                        {saving ? 'Saving…' : 'Save & Continue'}
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 4: Review & Submit */}
              <Step>
                <StepLabel>Review & Submit</StepLabel>
                <StepContent>
                  <Box sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Please review your information and submit. HR will verify your details and be in touch.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Don't forget to also complete the document upload and policy acknowledgement tasks listed below — HR will check these separately.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button onClick={() => setActiveStep(s => s - 1)}>Back</Button>
                      <Button variant="contained" startIcon={<Send />} onClick={submitAll} disabled={saving}
                        sx={{ bgcolor: '#2e7d32' }}>
                        {saving ? 'Submitting…' : 'Submit Pre-Boarding'}
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>

            {/* Document & policy tasks */}
            {data?.tasks && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>📎 Documents & Policies</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  These items don't have an online form — please bring physical copies or email them to HR.
                </Typography>
                {data.tasks.filter((t: any) => ['document_upload', 'policy_sign_off', 'welcome_survey'].includes(t.taskType)).map((t: any) => (
                  <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {t.isCompleted
                      ? <CheckCircle fontSize="small" color="success" />
                      : <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #bbb' }} />}
                    <Box>
                      <Typography variant="body2" sx={{ textDecoration: t.isCompleted ? 'line-through' : 'none', opacity: t.isCompleted ? 0.6 : 1 }}>
                        {t.title} {t.isRequired && <span style={{ color: '#b71c1c', fontSize: '0.7rem' }}>*required</span>}
                      </Typography>
                      {t.description && <Typography variant="caption" color="text.secondary">{t.description}</Typography>}
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
